/**
 * Normalise the shape of `err.response.data.detail` returned by our
 * FastAPI checkout endpoints so React never tries to render a raw
 * object (which triggers "Objects are not valid as a React child" —
 * error #31 — and crashes the toast). The backend may return any of:
 *
 *   - `null` / `undefined`         (network failure / non-JSON response)
 *   - `"Some plain string"`        (HTTPException(detail="…"))
 *   - `{ code, message, ... }`     (HTTPException(detail={code:…}))
 *   - `{ msg, type, loc }`         (Pydantic validation errors)
 *   - `[ { ... } ]`                (Pydantic validation error list)
 *
 * Returns `{ code, message, isEmailNotVerified, status, raw }` where
 * `message` is ALWAYS a safe string suitable for a toast/banner and
 * `code` is either a machine code string (`"email_not_verified"`,
 * `"cart_empty"`, …) or `null`.
 */
export function normalizeCheckoutError(err, fallback = "Checkout failed") {
  const status = err?.response?.status ?? null;
  const raw = err?.response?.data?.detail;

  // No response payload — likely network / CORS / 5xx with empty body.
  if (raw == null) {
    return { code: null, message: fallback, isEmailNotVerified: false, status, raw: null };
  }

  // Plain string detail.
  if (typeof raw === "string") {
    return { code: null, message: raw, isEmailNotVerified: false, status, raw };
  }

  // Pydantic validation error list — pick the first message.
  if (Array.isArray(raw)) {
    const first = raw.find((r) => r && (r.msg || r.message));
    const msg = first?.msg || first?.message || fallback;
    return { code: null, message: String(msg), isEmailNotVerified: false, status, raw };
  }

  // Object detail — extract code + human message safely.
  if (typeof raw === "object") {
    const code = typeof raw.code === "string" ? raw.code : null;
    const message =
      (typeof raw.message === "string" && raw.message) ||
      (typeof raw.msg === "string" && raw.msg) ||
      (typeof raw.detail === "string" && raw.detail) ||
      fallback;
    return {
      code,
      message,
      isEmailNotVerified: code === "email_not_verified",
      status,
      raw,
    };
  }

  return { code: null, message: fallback, isEmailNotVerified: false, status, raw };
}
